import { QuestionSet, QuestionItem, QuestionOption, QuestionSetQuota, QuestionItemQuota } from '../types';
import { getStoredToken, getAuthHeaders, customFetch } from './apiService';
import { getFriendlyErrorMessage, FRIENDLY_ERROR_MAP } from '../utils/aiErrorUtils';



const API_V1_QUESTIONS = '/api/v1/questions';

export interface QuestionSetsResponse {
  success: boolean;
  message?: string;
  sets: QuestionSet[];
  quota?: QuestionSetQuota;
}

export interface QuestionSetDetailResponse {
  success: boolean;
  message?: string;
  set: QuestionSet;
  questions: QuestionItem[];
  quota?: QuestionItemQuota;
}

export interface SingleQuestionResponse {
  success: boolean;
  message?: string;
  question?: QuestionItem;
  currentTotal?: number;
}

export interface BulkQuestionsResponse {
  success: boolean;
  message?: string;
  count?: number;
  questions?: QuestionItem[];
  totalInSet?: number;
}

export interface ImportExcelResponse {
  success: boolean;
  message?: string;
  count?: number;
  questions?: QuestionItem[];
  totalInSet?: number;
}

/**
 * 1. NHÓM API QUẢN LÝ BỘ CÂU HỎI (QUESTION SETS)
 */

/**
 * Lấy danh sách "Kho của tôi": GET /api/v1/questions/sets/me
 */
export const getMyQuestionSetsApi = async (params?: {
  search?: string;
  subject?: string;
  grade?: string;
}): Promise<QuestionSetsResponse> => {
  const query = new URLSearchParams();
  if (params?.search?.trim()) query.set('search', params.search.trim());
  if (params?.subject && params.subject !== 'all') query.set('subject', params.subject);
  if (params?.grade && params.grade !== 'all') query.set('grade', params.grade);

  const queryString = query.toString();
  const url = `${API_V1_QUESTIONS}/sets/me${queryString ? `?${queryString}` : ''}`;

  const res = await customFetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
    credentials: 'include'
  });

  const data = await res.json();
  if (!res.ok || !data.success) {
    throw new Error(data.message || 'Không thể tải danh sách bộ câu hỏi cá nhân.');
  }
  return data;
};

/**
 * Lấy danh sách "Thư viện cộng đồng": GET /api/v1/questions/sets/public
 */
export const getPublicQuestionSetsApi = async (params?: {
  search?: string;
  subject?: string;
  grade?: string;
}): Promise<QuestionSetsResponse> => {
  const query = new URLSearchParams();
  if (params?.search?.trim()) query.set('search', params.search.trim());
  if (params?.subject && params.subject !== 'all') query.set('subject', params.subject);
  if (params?.grade && params.grade !== 'all') query.set('grade', params.grade);

  const queryString = query.toString();
  const url = `${API_V1_QUESTIONS}/sets/public${queryString ? `?${queryString}` : ''}`;

  const res = await customFetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
    credentials: 'include'
  });

  const data = await res.json();
  if (!res.ok || !data.success) {
    throw new Error(data.message || 'Không thể tải danh sách thư viện cộng đồng.');
  }
  return data;
};

/**
 * Tạo bộ câu hỏi mới: POST /api/v1/questions/sets
 */
export const createQuestionSetApi = async (payload: {
  name?: string;
  title?: string;
  subject: string;
  grade: string;
  isPublic?: boolean;
}): Promise<{ success: boolean; message?: string; set: QuestionSet; quota?: QuestionSetQuota }> => {
  const title = (payload.title || payload.name || '').trim();
  const res = await customFetch(`${API_V1_QUESTIONS}/sets`, {
    method: 'POST',
    headers: getAuthHeaders(),
    credentials: 'include',
    body: JSON.stringify({
      title,
      subject: payload.subject,
      grade: payload.grade,
      isPublic: payload.isPublic
    })
  });

  const data = await res.json();
  if (!res.ok || !data.success) {
    throw new Error(data.message || 'Không thể tạo bộ câu hỏi.');
  }
  return data;
};

/**
 * Lấy chi tiết bộ câu hỏi & danh sách câu hỏi bên trong: GET /api/v1/questions/sets/:setId
 */
export const getQuestionSetDetailApi = async (setId: string): Promise<QuestionSetDetailResponse> => {
  const res = await customFetch(`${API_V1_QUESTIONS}/sets/${setId}`, {
    method: 'GET',
    headers: getAuthHeaders(),
    credentials: 'include'
  });

  const data = await res.json();
  if (!res.ok || !data.success) {
    throw new Error(data.message || 'Không thể tải thông tin bộ câu hỏi.');
  }
  return data;
};

/**
 * Chỉnh sửa bộ câu hỏi: PUT /api/v1/questions/sets/:setId
 */
export const updateQuestionSetApi = async (
  setId: string,
  payload: {
    name?: string;
    title?: string;
    subject?: string;
    grade?: string;
    isPublic?: boolean;
  }
): Promise<{ success: boolean; message?: string; set: QuestionSet }> => {
  const body: Record<string, any> = {};
  if (payload.title !== undefined || payload.name !== undefined) {
    body.title = payload.title !== undefined ? payload.title : payload.name;
  }
  if (payload.subject !== undefined) body.subject = payload.subject;
  if (payload.grade !== undefined) body.grade = payload.grade;
  if (payload.isPublic !== undefined) body.isPublic = payload.isPublic;

  const res = await customFetch(`${API_V1_QUESTIONS}/sets/${setId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    credentials: 'include',
    body: JSON.stringify(body)
  });

  const data = await res.json();
  if (!res.ok || !data.success) {
    throw new Error(data.message || 'Không thể cập nhật bộ câu hỏi.');
  }
  return data;
};

/**
 * Xóa bộ câu hỏi: DELETE /api/v1/questions/sets/:setId
 */
export const deleteQuestionSetApi = async (setId: string): Promise<{ success: boolean; message?: string }> => {
  const res = await customFetch(`${API_V1_QUESTIONS}/sets/${setId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
    credentials: 'include'
  });

  const data = await res.json();
  if (!res.ok || !data.success) {
    throw new Error(data.message || 'Không thể xóa bộ câu hỏi.');
  }
  return data;
};

/**
 * Nhân bản bộ câu hỏi từ cộng đồng: POST /api/v1/questions/sets/:setId/clone
 */
export const cloneQuestionSetApi = async (setId: string): Promise<{
  success: boolean;
  message?: string;
  set?: QuestionSet;
  clonedQuestionCount?: number;
}> => {
  const res = await customFetch(`${API_V1_QUESTIONS}/sets/${setId}/clone`, {
    method: 'POST',
    headers: getAuthHeaders(),
    credentials: 'include'
  });

  const data = await res.json();
  if (!res.ok || !data.success) {
    throw new Error(data.message || 'Không thể nhân bản bộ câu hỏi.');
  }
  return data;
};

/**
 * 2. NHÓM API QUẢN LÝ CÂU HỎI BÊN TRONG BỘ (QUESTIONS)
 */

/**
 * Thêm 1 câu hỏi thủ công: POST /api/v1/questions/manual (Body cần truyền `setId`)
 */
export const addManualQuestionApi = async (payload: {
  setId: string;
  questionText: string;
  options: QuestionOption[];
  explanation?: string;
  difficulty?: string;
}): Promise<SingleQuestionResponse> => {
  const res = await customFetch(`${API_V1_QUESTIONS}/manual`, {
    method: 'POST',
    headers: getAuthHeaders(),
    credentials: 'include',
    body: JSON.stringify(payload)
  });

  const data = await res.json();
  if (!res.ok || !data.success) {
    throw new Error(data.message || 'Không thể thêm câu hỏi.');
  }
  return data;
};

/**
 * Chỉnh sửa 1 câu hỏi: PUT /api/v1/questions/:id
 */
export const updateQuestionApi = async (
  questionId: string,
  payload: {
    questionText?: string;
    options?: QuestionOption[];
    explanation?: string;
    difficulty?: string;
  }
): Promise<SingleQuestionResponse> => {
  const res = await customFetch(`${API_V1_QUESTIONS}/${questionId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    credentials: 'include',
    body: JSON.stringify(payload)
  });

  const data = await res.json();
  if (!res.ok || !data.success) {
    throw new Error(data.message || 'Không thể cập nhật câu hỏi.');
  }
  return data;
};

/**
 * Xóa 1 câu hỏi: DELETE /api/v1/questions/:id
 */
export const deleteQuestionApi = async (questionId: string): Promise<{ success: boolean; message?: string }> => {
  const res = await customFetch(`${API_V1_QUESTIONS}/${questionId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
    credentials: 'include'
  });

  const data = await res.json();
  if (!res.ok || !data.success) {
    throw new Error(data.message || 'Không thể xóa câu hỏi.');
  }
  return data;
};

/**
 * Lưu danh sách câu hỏi do AI tạo ra (hoặc danh sách câu hỏi dạng mảng): 
 * POST /api/v1/questions/bulk-add
 * Body bắt buộc theo định dạng: { setId: string, questions: Array }
 */
export const bulkAddQuestionsApi = async (payload: {
  setId: string;
  questions: Array<{
    questionText: string;
    options: QuestionOption[];
    explanation?: string;
    difficulty?: string;
  }>;
}): Promise<BulkQuestionsResponse> => {
  const res = await customFetch(`${API_V1_QUESTIONS}/bulk-add`, {
    method: 'POST',
    headers: getAuthHeaders(),
    credentials: 'include',
    body: JSON.stringify({
      setId: payload.setId,
      questions: payload.questions
    })
  });

  const data = await res.json();
  if (!res.ok || !data.success) {
    throw new Error(data.message || 'Không thể lưu danh sách câu hỏi.');
  }
  return data;
};

/**
 * Import bằng file Excel: POST /api/v1/questions/import-excel
 * Dùng FormData, gửi file lên field tên là `file`, kèm theo trường `setId`
 */
export const importExcelQuestionsApi = async (
  file: File,
  setId: string
): Promise<ImportExcelResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('setId', setId);

  const token = getStoredToken();
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await customFetch(`${API_V1_QUESTIONS}/import-excel`, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: formData
  });

  const data = await res.json();
  if (!res.ok || !data.success) {
    throw new Error(data.message || 'Không thể import file Excel.');
  }
  return data;
};

/**
 * 3. CẤU TRÚC PAYLOAD CHUNG CHO MỌI API AI (PHẦN 3)
 * Phục vụ kiến trúc xử lý AI tập trung tại Backend
 */
export interface AiCommonPayload {
  prompt: string;
  fileUrls?: string[];
  fileUrl?: string;
  requestedModel?: string;
  [key: string]: any;
}

export interface AiGenerateQuestionsPayload extends AiCommonPayload {
  subject?: string;
  grade?: string;
  difficulty?: string;
  questionCount?: number;
  targetSetId?: string;
}

/**
 * Gọi API sinh câu hỏi tập trung phía Backend (Không dùng SDK phía Client)
 */
export const generateQuestionsWithAiApi = async (
  payload: AiGenerateQuestionsPayload
): Promise<{
  success: boolean;
  message?: string;
  code?: string;
  isQuotaExceeded?: boolean;
  modelUsed?: string;
  questions: Array<{
    questionText: string;
    questionMedia?: string;
    options: QuestionOption[];
    explanation: string;
    difficulty: string;
  }>;
}> => {
  const res = await customFetch('/api/v1/questions/ai-generate', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    },
    credentials: 'include',
    body: JSON.stringify(payload)
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.success) {
    const friendly = getFriendlyErrorMessage(data.code || data.message || res.status);
    const error: any = new Error(friendly);
    error.code = data.code;
    error.status = res.status;
    error.rawMessage = data.message;
    error.friendlyMessage = friendly;
    error.isQuotaExceeded = data.isQuotaExceeded;
    throw error;
  }
  return data;
};

/**
 * Cache tên model Gemini và danh sách models toàn hệ thống trong phiên làm việc
 */
export interface GeminiModelsConfig {
  models: string[];
  model: string;
}

let cachedGeminiConfig: GeminiModelsConfig | null = null;

export const getGeminiModelsConfigApi = async (): Promise<GeminiModelsConfig> => {
  if (cachedGeminiConfig) {
    return cachedGeminiConfig;
  }
  try {
    const res = await customFetch('/api/config/gemini-model');
    const data = await res.json();
    if (data && data.success) {
      const models = Array.isArray(data.models) && data.models.length > 0
        ? data.models
        : (data.model ? [data.model] : ["gemini-2.5-flash", "gemini-1.5-flash", "gemini-1.5-pro", "gemini-2.0-flash"]);
      const model = data.model || models[0] || "gemini-2.5-flash";
      cachedGeminiConfig = { models, model };
      return cachedGeminiConfig;
    }
  } catch (err) {
    console.warn('Không thể lấy cấu hình dynamic model, sử dụng fallback:', err);
  }
  const fallback: GeminiModelsConfig = {
    models: ["gemini-2.5-flash", "gemini-1.5-flash", "gemini-1.5-pro", "gemini-2.0-flash"],
    model: "gemini-2.5-flash"
  };
  return fallback;
};

export const getGeminiActiveModelApi = async (): Promise<string> => {
  const config = await getGeminiModelsConfigApi();
  return config.model;
};


